import { useState, useRef, useLayoutEffect, useId } from "react";
import { createPortal } from "react-dom";
import useClickOutside from "../../hooks/useClickOutside";
import {
  TRIGGER_SHARED_CLS,
  triggerBorderCls,
  DROPDOWN_LIST_CLS,
  rowSepCls,
  rowBgCls,
  DropdownChevron,
  OptionDescription,
} from "./dropdownShared";

const Select = ({
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  error = false,
  disabled = false,
  compact = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const listboxId = useId();

  // Close when clicking outside either the trigger or the portal list
  useClickOutside([containerRef, listRef], () => setOpen(false));

  // Recalculate portal position whenever the dropdown opens
  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }, [open]);

  const selected = options.find((o) => o.value === value);

  const dropdownList = open && coords && createPortal(
    <div
      id={listboxId}
      ref={listRef}
      role="listbox"
      className={DROPDOWN_LIST_CLS}
      style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 9999 }}
    >
      {options.map((option, i) => (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={value === option.value}
          onClick={() => { onChange(option.value); setOpen(false); }}
          className={`w-full px-4 py-2.5 text-left transition-colors ${rowSepCls(i)} ${
            value === option.value
              ? `${rowBgCls(true)} text-ink-primary`
              : `${rowBgCls(false)} text-ink-ink dark:text-ink-white`
          }`}
        >
          <div className="text-[13px] font-medium">{option.label}</div>
          <OptionDescription text={option.description} />
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={selected ? selected.label : placeholder}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-invalid={error || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`${compact ? "px-2.5 py-1.5 !rounded-md" : "px-3 py-2.5"} ${TRIGGER_SHARED_CLS} ${triggerBorderCls(error)} ${
          selected ? "text-ink-ink dark:text-ink-white" : "text-ink-gray-400"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <DropdownChevron open={open} />
      </button>
      {dropdownList}
    </div>
  );
};

export default Select;

